import React, { useState, useEffect, useCallback } from 'react';
import { Loader, Save, Lock, LockOpen, KeyRound, Wand2, Eye, EyeOff, Copy } from 'lucide-react';
import { inputStyle, RED, fileToBase64 } from '../utils/atoShared';

// ── The tax vault, shared by the Tax returns and Tax documents tabs ──────────────
// One password, one unlock token (renewed on every call; 15 idle minutes locks it), one backend (ato-returns-sb). The token
// lives in sessionStorage only, so unlocking either tab unlocks both, and closing the
// browser tab locks them. The server enforces the lock: every call carries the token
// and a missing or stale one comes back VAULT_LOCKED.

export const VAULT_API = '/.netlify/functions/ato-returns-sb';
const TOKEN_KEY = 'hp_ato_returns_unlock';
export const VAULT_MAX_BYTES = 4 * 1024 * 1024; // one call's cap (Netlify's 6 MB body, less base64's third)
const PART_BYTES = 3 * 1024 * 1024;             // a bigger file goes up in pieces this size
const MAX_FILE_BYTES = 75 * 1024 * 1024;        // the function's 25-piece guard
const READ_BYTES = 3.8 * 1024 * 1024;           // what Claude is sent to read (one call)

// Every successful call returns a renewed token (15 minutes from NOW): keep it and tell
// the tab, so the vault locks after 15 idle minutes, not mid-upload.
const RENEW_EVENT = 'hp-ato-vault-renew';
export const vaultCall = (action, payload = {}) =>
  fetch(VAULT_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
    .then((r) => r.json())
    .then((r) => {
      if (r && r.success && r.unlock && payload.unlock && readToken()) {
        writeToken(r.unlock);
        try { window.dispatchEvent(new CustomEvent(RENEW_EVENT, { detail: r.unlock })); } catch { /* ignore */ }
      }
      return r;
    });

const readToken = () => { try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; } };
const writeToken = (t) => { try { if (t) sessionStorage.setItem(TOKEN_KEY, t); else sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } };
// `<LOGIN>.<exp>.<sig>` — the expiry is readable, so the page can lock itself on time.
const tokenExp = (t) => (parseInt(String(t).split('.')[1], 10) || 0) * 1000;

export function checkVaultFile(f, notify) {
  if (!f) return false;
  if (!(f.type.startsWith('image/') || f.type === 'application/pdf')) { notify?.(`${f.name}: attach a PDF, photo, screenshot or a .zip of them`, 'error'); return false; }
  if (f.size > MAX_FILE_BYTES) { notify?.(`${f.name} is over 75 MB`, 'error'); return false; }
  return true;
}

function bytesToBase64(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
const isZip = (f) => /\.zip$/i.test(f.name) || /zip/.test(f.type);
const typeFor = (name) => (/\.pdf$/i.test(name) ? 'application/pdf' : /\.png$/i.test(name) ? 'image/png' : /\.jpe?g$/i.test(name) ? 'image/jpeg' : /\.heic$/i.test(name) ? 'image/heic' : '');

// A dropped .zip becomes the PDFs / images inside it (folders flattened, macOS
// __MACOSX and dot-files skipped); anything else passes through. Unzipped in the
// browser, so the archive itself never leaves the computer.
export async function expandZips(fileList, notify) {
  const out = [];
  for (const f of Array.from(fileList || [])) {
    if (!isZip(f)) { out.push(f); continue; }
    try {
      const { default: JSZip } = await import('jszip');
      const zip = await JSZip.loadAsync(f);
      const entries = Object.values(zip.files).filter((e) => !e.dir && !/(^|\/)(__MACOSX|\.)/.test(e.name));
      let skipped = 0;
      for (const e of entries) {
        const name = e.name.split('/').pop();
        const type = typeFor(name);
        if (!type) { skipped++; continue; }
        out.push(new File([await e.async('blob')], name, { type }));
      }
      if (skipped) notify?.(`${f.name}: skipped ${skipped} file${skipped === 1 ? '' : 's'} that aren't PDFs or images`, 'info');
    } catch (e) { notify?.(`Couldn't open ${f.name}: ${e.message}`, 'error'); }
  }
  return out;
}

// The bytes we keep: a PDF exactly as it is (it may be signed), a photo turned upright
// and re-encoded like everywhere else in the engine.
async function fileBytes(f) {
  if (f.type === 'application/pdf') return { bytes: new Uint8Array(await f.arrayBuffer()), contentType: 'application/pdf' };
  const { base64, contentType } = await fileToBase64(f);
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return { bytes, contentType };
}

// What Claude reads: the whole file when it fits in one call, else (or when `maxPages`
// asks) a copy of the first pages — on these returns page 1 is the summary that carries
// the figures. The stored file is always the untouched original.
async function readableBase64(bytes, contentType, maxPages) {
  if (contentType !== 'application/pdf') return bytes.length <= READ_BYTES ? bytesToBase64(bytes) : null;
  if (!maxPages && bytes.length <= READ_BYTES) return bytesToBase64(bytes);
  try {
    const { PDFDocument } = await import('pdf-lib');
    const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    // Page by page: keep each of the first pages that still fits, skip one that's too big
    // on its own (a scanned signature page can be 4 MB while the summary is 50 KB).
    const keep = [];
    let total = 0;
    for (let i = 0; i < Math.min(maxPages || 12, src.getPageCount()); i++) {
      const one = await PDFDocument.create();
      one.addPage((await one.copyPages(src, [i]))[0]);
      const size = (await one.save()).length;
      if (total + size <= READ_BYTES) { keep.push(i); total += size; }
    }
    if (keep.length) {
      const out = await PDFDocument.create();
      (await out.copyPages(src, keep)).forEach((p) => out.addPage(p));
      const b = await out.save();
      if (b.length <= READ_BYTES) return bytesToBase64(b);
    }
  } catch { /* fall through to the whole file if it fits */ }
  return bytes.length <= READ_BYTES ? bytesToBase64(bytes) : null;
}

// Prepare one file for the vault: what Claude can read, and the save payload — inline when
// it fits one call, otherwise uploaded now in encrypted pieces (`putPart`) and referenced.
export async function prepareVaultFile(f, { unlock, maxReadPages, onProgress } = {}) {
  const { bytes, contentType } = await fileBytes(f);
  const readBase64 = await readableBase64(bytes, contentType, maxReadPages);
  const upload = async () => {
    if (bytes.length <= VAULT_MAX_BYTES) return { fileBase64: bytesToBase64(bytes), fileContentType: contentType, fileName: f.name };
    const uploadId = crypto.randomUUID();
    const count = Math.ceil(bytes.length / PART_BYTES);
    for (let i = 0; i < count; i++) {
      onProgress?.(`uploading ${i + 1} of ${count}`);
      const r = await vaultCall('putPart', { unlock, uploadId, index: i, base64: bytesToBase64(bytes.subarray(i * PART_BYTES, (i + 1) * PART_BYTES)) });
      if (r.error === 'VAULT_LOCKED') throw new Error('VAULT_LOCKED');
      if (!r.success) throw new Error(r.error || `piece ${i + 1} failed`);
    }
    return { fileParts: { uploadId, count, size: bytes.length }, fileContentType: contentType, fileName: f.name };
  };
  return { readBase64, contentType, size: bytes.length, upload };
}

// status / unlock / lock, plus locking on the token's own expiry so the page never
// shows records the server would refuse.
export function useVault(notify) {
  const [status, setStatus] = useState(null);   // null = checking | { hasPassword }
  const [unlock, setUnlock] = useState(readToken);
  const lock = useCallback(() => { writeToken(''); setUnlock(''); }, []);
  const onUnlocked = useCallback((t) => { writeToken(t); setUnlock(t); setStatus({ hasPassword: true }); }, []);

  useEffect(() => {
    vaultCall('status', { unlock: readToken() })
      .then((r) => { if (r.success) { setStatus({ hasPassword: r.hasPassword }); if (!r.unlocked) lock(); } else notify?.(r.error || 'Could not reach the vault', 'error'); })
      .catch((e) => notify?.(e.message, 'error'));
  }, [lock, notify]);

  useEffect(() => {
    const onRenew = (e) => setUnlock((cur) => (cur ? e.detail : cur));
    window.addEventListener(RENEW_EVENT, onRenew);
    return () => window.removeEventListener(RENEW_EVENT, onRenew);
  }, []);

  useEffect(() => {
    if (!unlock) return undefined;
    const ms = tokenExp(unlock) - Date.now();
    if (ms <= 0) { lock(); return undefined; }
    const t = setTimeout(() => { lock(); notify?.('Tax vault locked after 15 minutes unused', 'info'); }, ms);
    return () => clearTimeout(t);
  }, [unlock, lock, notify]);

  return { status, unlock, lock, onUnlocked };
}

// The window opens inside the click (a popup opened after an await is blocked), then
// the decrypted file is poured into it as a blob: URL — never a link to the plaintext.
export async function openVaultFile(action, payload, { unlock, onLocked, notify }) {
  const w = window.open('', '_blank');
  try {
    const r = await vaultCall(action, { unlock, ...payload });
    if (r.error === 'VAULT_LOCKED') { w?.close(); onLocked(); return; }
    if (!r.success) { w?.close(); notify?.(r.error || 'Could not open it', 'error'); return; }
    // A big file comes back as a piece count: fetch each piece and join them in order.
    const chunks = [];
    if (r.parts) {
      for (let i = 0; i < r.parts; i++) {
        const p = await vaultCall(action, { unlock, ...payload, part: i });
        if (p.error === 'VAULT_LOCKED') { w?.close(); onLocked(); return; }
        if (!p.success) { w?.close(); notify?.(p.error || 'Could not open it', 'error'); return; }
        chunks.push(p.base64);
      }
    } else chunks.push(r.base64);
    const parts = chunks.map((b64) => {
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return bytes;
    });
    const url = URL.createObjectURL(new Blob(parts, { type: r.contentType || 'application/pdf' }));
    if (w) w.location.href = url; else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { w?.close(); notify?.(e.message, 'error'); }
}

// ── password managers ───────────────────────────────────────────────────────────
// The vault saves under its OWN username on hollypaul.com, so Google / Apple Passwords
// keep it as a second entry instead of overwriting the engine login (BOUVENGP2).
// Every vault form carries this read-only username field + autocomplete hints, which
// is what lets Chrome, Safari and the iCloud Passwords extension offer to save,
// suggest and later fill it.
export const VAULT_USERNAME = 'Tax vault (BOUVENGP2)';

// A strong password the engine suggests: 4 groups of 5 from an alphabet without the
// look-alikes (0/O, 1/l/I), so it can be read off a phone and typed if it ever has to be.
// ~117 bits. crypto.getRandomValues, with rejection sampling so no character is favoured.
const ALPHABET = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function suggestPassword() {
  const out = [];
  const buf = new Uint8Array(1);
  const limit = 256 - (256 % ALPHABET.length);
  while (out.length < 20) {
    crypto.getRandomValues(buf);
    if (buf[0] < limit) out.push(ALPHABET[buf[0] % ALPHABET.length]);
  }
  return [0, 5, 10, 15].map((i) => out.slice(i, i + 5).join('')).join('-');
}

// Chrome (and Edge) take a saved credential straight from the page: this raises the
// browser's own "Save password?" prompt into Google Password Manager. Safari has no
// PasswordCredential, but it offers to save to Apple Passwords on its own when the
// new-password form is submitted and goes away, which is what happens on success.
export async function offerToSave(password) {
  try {
    if (typeof window !== 'undefined' && window.PasswordCredential && navigator.credentials?.store) {
      await navigator.credentials.store(new window.PasswordCredential({ id: VAULT_USERNAME, password, name: VAULT_USERNAME }));
    }
  } catch { /* the user dismissed it, or the browser said no — the password is set either way */ }
}

const UsernameField = () => (
  <input type="text" className="input" name="username" autoComplete="username" value={VAULT_USERNAME} readOnly
    style={{ ...inputStyle, color: 'var(--hp-dim)', fontSize: '.8rem' }} aria-label="Saved in your password manager as" />
);

// The password box with show/hide; `onSuggest` adds the engine's "Suggest" button.
function PasswordInput({ value, onChange, placeholder, autoComplete, autoFocus, shown, setShown, onSuggest }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input type={shown ? 'text' : 'password'} className="input" style={{ ...inputStyle, fontFamily: shown ? 'var(--font-mono)' : undefined }}
        name={autoComplete === 'new-password' ? 'new-password' : 'password'} autoComplete={autoComplete} autoFocus={autoFocus}
        placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      <button type="button" className="btn btn-ghost btn-sm" title={shown ? 'Hide' : 'Show'} onClick={() => setShown(!shown)}>{shown ? <EyeOff size={14} /> : <Eye size={14} />}</button>
      {onSuggest && <button type="button" className="btn btn-secondary btn-sm" title="Suggest a strong password" onClick={onSuggest} style={{ whiteSpace: 'nowrap' }}><Wand2 size={14} /> Suggest</button>}
    </div>
  );
}

// Shown under a suggested password: copy it, and where it will be saved.
function SuggestedNote({ pw, notify }) {
  const copy = async () => { try { await navigator.clipboard.writeText(pw); notify?.('Copied', 'success'); } catch { notify?.('Copy failed — select it and copy', 'error'); } };
  return (
    <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <code style={{ fontSize: '.9rem', color: 'var(--hp-text)', letterSpacing: '.03em' }}>{pw}</code>
        <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '1px 6px' }} onClick={copy}><Copy size={12} /> Copy</button>
      </div>
      When you click the button below your browser offers to save it as <b>{VAULT_USERNAME}</b>: Chrome to Google Password Manager,
      Safari (or Chrome with iCloud Passwords) to Apple Passwords. Say yes. It fills itself in next time.
    </div>
  );
}

// ── the lock screen ─────────────────────────────────────────────────────────────
export function VaultLock({ hasPassword, onUnlocked, notify, what = 'Tax returns and documents' }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [shown, setShown] = useState(false);
  const [suggested, setSuggested] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const suggest = () => { const p = suggestPassword(); setPw(p); setPw2(p); setSuggested(p); setShown(true); };
  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!hasPassword && pw !== pw2) { setErr("The two passwords don't match."); return; }
    setBusy(true);
    try {
      const r = await vaultCall(hasPassword ? 'unlock' : 'setPassword', { password: pw });
      if (r.success && r.unlock) {
        if (!hasPassword) { await offerToSave(pw); notify?.('Vault password set', 'success'); }
        setPw(''); setPw2(''); onUnlocked(r.unlock);
      }
      else setErr(r.error || 'Could not unlock');
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="card" style={{ padding: 28, maxWidth: 460 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Lock size={20} color="var(--hp-gold)" />
        <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{hasPassword ? `${what} are locked` : 'Set a vault password'}</div>
      </div>
      <p style={{ color: 'var(--hp-dim)', fontSize: '.86rem', marginTop: 0 }}>
        {hasPassword
          ? 'Enter your vault password. It opens tax returns and tax documents, and locks again after 15 minutes unused or when you close this tab.'
          : 'Tax returns and ATO correspondence carry your TFN, so they sit behind a password of their own, separate from your engine login. Click Suggest for a strong one and let your browser save it, or choose one you don’t use anywhere else (8 characters or more). Everything in the vault is stored encrypted.'}
      </p>
      <form onSubmit={submit} method="post" action="#" style={{ display: 'grid', gap: 10 }}>
        <UsernameField />
        <PasswordInput value={pw} onChange={(v) => { setPw(v); if (v !== suggested) setSuggested(''); }} autoFocus shown={shown} setShown={setShown}
          autoComplete={hasPassword ? 'current-password' : 'new-password'} placeholder={hasPassword ? 'Vault password' : 'New vault password'}
          onSuggest={hasPassword ? null : suggest} />
        {!hasPassword && (
          <input type={shown ? 'text' : 'password'} className="input" style={{ ...inputStyle, fontFamily: shown ? 'var(--font-mono)' : undefined }} name="confirm-password" autoComplete="new-password" placeholder="Type it again" value={pw2} onChange={(e) => setPw2(e.target.value)} />
        )}
        {!hasPassword && suggested && pw === suggested && <SuggestedNote pw={suggested} notify={notify} />}
        {err && <div style={{ color: RED, fontSize: '.84rem' }}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy || pw.length < (hasPassword ? 1 : 8)}>
          {busy ? <Loader size={14} className="spin" /> : <LockOpen size={14} />} {hasPassword ? 'Unlock' : 'Set password and unlock'}
        </button>
      </form>
    </div>
  );
}

export function ChangePassword({ onDone, onClose, notify }) {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [next2, setNext2] = useState('');
  const [shown, setShown] = useState(false);
  const [suggested, setSuggested] = useState('');
  const [busy, setBusy] = useState(false);
  const suggest = () => { const p = suggestPassword(); setNext(p); setNext2(p); setSuggested(p); setShown(true); };
  const submit = async (e) => {
    e.preventDefault();
    if (next !== next2) { notify?.("The new passwords don't match", 'error'); return; }
    setBusy(true);
    try {
      const r = await vaultCall('changePassword', { current: cur, next });
      if (r.success) { await offerToSave(next); notify?.('Vault password changed', 'success'); onDone(r.unlock); }
      else notify?.(r.error || 'Could not change it', 'error');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} method="post" action="#" className="card" style={{ padding: 16, marginBottom: 14, display: 'grid', gap: 8, maxWidth: 460 }}>
      <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}><KeyRound size={15} /> Change vault password</div>
      <UsernameField />
      <input type="password" className="input" style={inputStyle} name="password" autoComplete="current-password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
      <PasswordInput value={next} onChange={(v) => { setNext(v); if (v !== suggested) setSuggested(''); }} shown={shown} setShown={setShown}
        autoComplete="new-password" placeholder="New password (8+ characters)" onSuggest={suggest} />
      <input type={shown ? 'text' : 'password'} className="input" style={{ ...inputStyle, fontFamily: shown ? 'var(--font-mono)' : undefined }} name="confirm-password" autoComplete="new-password" placeholder="New password again" value={next2} onChange={(e) => setNext2(e.target.value)} />
      {suggested && next === suggested && <SuggestedNote pw={suggested} notify={notify} />}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !cur || next.length < 8}>{busy ? <Loader size={13} className="spin" /> : <Save size={13} />} Change</button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}
